import React from 'react';

import { AttentionMessage, WarningMessage } from '@eika/attention-message';
import { IconTypes } from '@eika/icon';
import { Input } from '@eika/input';

import { useFormContext } from '../../hooks/useForm';
import useTranslation from '../../hooks/useTranslation';
import { UpdateContactInfoAction, UpdateContactInfoActionType } from './form/actions';
import { UpdateContactInfoState } from './form/state';
import { Error } from './form/validate';

function defaultFormatter(value: string) {
    return <span>{String(value)}</span>;
}

export type UpdateWarning =
    | {
          type: 'attention';
          text: string;
          link?: React.JSX.Element;
      }
    | {
          type: 'warning';
          text: string;
          link: React.JSX.Element;
      }
    | null;

type Props = {
    stateKey: 'email' | 'mobile';
    canUpdate?: boolean;
    formatter?: (value: string) => React.ReactNode;
    disabled?: boolean;
    warning: UpdateWarning;
};

export default ({ stateKey, canUpdate = true, formatter = defaultFormatter, disabled, warning }: Props) => {
    const translated = useTranslation();
    const { handleChange, state, errors } = useFormContext<
        UpdateContactInfoState,
        UpdateContactInfoAction,
        Error,
        UpdateContactInfoActionType
    >();
    return (
        <section className="update-customer-info__input">
            {canUpdate ? (
                <Input
                    label={translated.contactInfo[stateKey]}
                    type={stateKey === 'mobile' ? 'tel' : 'email'}
                    value={state[stateKey]}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleChange({ type: stateKey, payload: e.currentTarget.value })
                    }
                    error={errors[stateKey]}
                    disabled={disabled}
                    data-testid={stateKey === 'mobile' ? 'change-tel' : 'change-email'}
                />
            ) : (
                <section className="contact-information__wrapper">
                    <p className="contact-information__title bold">{translated.contactInfo[stateKey]}</p>
                    {state[stateKey] ? (
                        formatter(state[stateKey] ?? '')
                    ) : (
                        <span className="contact-information__value-missing">
                            {translated.contactInfo[`${stateKey}Missing`]}
                        </span>
                    )}
                    {warning && warning.type === 'attention' && (
                        <AttentionMessage
                            className="update-customer-info__warning"
                            icon={IconTypes.INFO_20}
                            type="info"
                            text={warning.text}
                            clickableItem={warning.link}
                        />
                    )}
                    {warning && warning.type === 'warning' && (
                        <WarningMessage
                            className="update-customer-info__warning"
                            icon={IconTypes.INFO_24}
                            text={warning.text}
                            clickableItem={warning.link}
                        />
                    )}
                </section>
            )}
        </section>
    );
};
